import { DataTable } from "@/components/admin/data-table";
import { BulkExportButton } from "@/components/admin/bulk-export-button";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { List } from "@/components/admin/list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Building2, ShieldCheck, SlidersHorizontal } from "lucide-react";
import {
  RecordContextProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useUnselectAll,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { TopToolbar } from "../layout/TopToolbar";
import { DoorListFilter } from "./DoorListFilter";
import { Explain } from "./Explain";
import { MARKETING_PATHS } from "./paths";

/**
 * The mailing universe — one row per physical door.
 *
 * Deliberately NOT the same thing as `companies`. 5,360 doors are on the
 * mailing list; only the ones that respond become companies. Zoho conflated
 * these and that is why Address ID ended up stored in the Fax field.
 *
 * Reads `door_directory`, which joins suppression, mail, promotion and review
 * state in one query. Fetching those per row would be an N+1 across 5,360 rows.
 *
 * NOTE FOR DROPS: this view intentionally includes doors that must not be
 * mailed, because a retired or opted-out door still needs to be visible and
 * explicable. A drop must select from `mailable_doors` instead — that is the
 * only view that answers "may we mail this?".
 */

type Door = {
  id: number;
  zip5: string;
  house_number: string;
  street: string;
  city: string | null;
  state: string | null;
  mail_to: string | null;
  wave: string | null;
  region: string | null;
  cell: string | null;
  is_mailable: boolean;
  is_suppressed: boolean;
  suppression_reason: string | null;
  company_id: number | null;
  company_name: string | null;
  touches_sent: number;
  last_touch: string | null;
  open_reviews: number;
  status: "mailable" | "suppressed" | "retired" | "no addressee";
  /** From `prospects`, the ratings made in the old HTML review page. Null for
   *  5,337 of 5,363 doors, so "unrated" is the normal state, not a verdict. */
  potential: "high" | "low" | null;
  account_type: string | null;
  /** high=1, unrated=2, low=3. Sorting on the WORD would put high before low by
   *  accident of the alphabet, and would silently break on a third value. */
  potential_rank: number;
  permit_count: number;
  industry_mix: string | null;
  /** Holds an Importer or Wholesaler permit as well as its DSP: 908 doors. */
  beyond_dsp: boolean | null;
  dsp_permit: string | null;
};

const STATUS_TONE: Record<
  Door["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  mailable: "secondary",
  suppressed: "destructive",
  retired: "outline",
  "no addressee": "outline",
};

/** Matches suppressions_reason_check in 001. Only opt-outs belong here --
 *  address QUALITY (mail drops, defunct permits) is overrides.suppress_address
 *  and is decided in the Review queue, not from this button. */

/**
 * Validate the next few addresses against the postal file.
 *
 * Ten at a time, deliberately. The list is 5,360 doors and nothing has ever
 * been checked; the first run exists to learn what PCM says and what it costs,
 * not to process the backlog. A "validate everything" button before anyone has
 * seen a single response would be a large action taken on no evidence.
 */
const ValidateButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "validate-addresses",
        { body: { limit: 10 } },
      );
      // supabase-js throws away the body of any non-2xx and reports it as a
      // transport failure; the real answer is on error.context.
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const body = ctx?.json ? await ctx.json().catch(() => null) : null;
        throw new Error(body?.error ?? error.message);
      }
      const r = data as {
        attempted?: number;
        by_status?: Record<string, number>;
        shape_recognized?: boolean;
        note?: string;
      };
      notify(
        r?.note ??
          `Checked ${r?.attempted ?? 0}: ` +
            Object.entries(r?.by_status ?? {})
              .map(([k, v]) => `${v} ${k}`)
              .join(", "),
        { type: r?.shape_recognized === false ? "warning" : "info" },
      );
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={run} disabled={busy}>
      <ShieldCheck className="size-4" />
      {busy ? "Checking…" : "Validate next 10 addresses"}
    </Button>
  );
};

/**
 * What you may do to a selection of doors.
 *
 * REPLACES THE DEFAULT SET, AND THAT IS HALF THE POINT.
 * DataTable's default `bulkActionButtons` is SelectAll + Export + **Delete**,
 * so this list has been offering to delete doors. Doors come from the TTB
 * pipeline via sync_to_db.py; deleting one here would either fail against a
 * view or destroy a row the next sync would resurrect, and neither is a thing
 * anybody wants a button for. There is no CreateButton on this list for the
 * same reason -- deletion had simply arrived by default rather than by choice.
 */
const DoorBulkActions = () => {
  const { selectedIds } = useListContext();
  const unselectAll = useUnselectAll("door_directory");
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const validateSelected = async () => {
    if (!selectedIds?.length) return;
    setBusy(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "validate-addresses",
        { body: { address_ids: selectedIds.map(Number) } },
      );
      // supabase-js discards the body of any non-2xx and reports it as a
      // transport error; the real answer is on error.context.
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const body = ctx?.json ? await ctx.json().catch(() => null) : null;
        throw new Error(body?.error ?? error.message);
      }
      const r = data as {
        attempted?: number;
        by_status?: Record<string, number>;
        not_checked?: number;
        shape_recognized?: boolean;
        note?: string;
      };
      const summary = Object.entries(r?.by_status ?? {})
        .map(([k, v]) => `${v} ${k}`)
        .join(", ");
      notify(
        r?.note ??
          `Checked ${r?.attempted ?? 0}: ${summary}` +
            (r?.not_checked
              ? ` · ${r.not_checked} over the cap were not checked`
              : ""),
        { type: r?.shape_recognized === false ? "warning" : "info" },
      );
      unselectAll();
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SelectAllButton />
      <BulkExportButton />
      <Button
        variant="outline"
        size="sm"
        onClick={validateSelected}
        disabled={busy || !selectedIds?.length}
      >
        <ShieldCheck className="size-4" />
        {busy ? "Checking…" : `Validate ${selectedIds?.length ?? 0}`}
      </Button>
    </>
  );
};

const DoorListActions = () => (
  <TopToolbar>
    <ValidateButton />
    {/* No CreateButton: doors come from the TTB pipeline via sync_to_db.py.
        Anything added here would be retired by the next sync, because a door
        absent from the workbook is marked is_mailable = false. */}
  </TopToolbar>
);

/**
 * The door, as a link into its own page.
 *
 * An explicit <Link> rather than the DataTable's `rowClick`. rowClick hands its
 * return value to react-admin's path resolution, which reads a string as a link
 * TYPE ("edit", "show") before it considers it a path -- and this resource
 * registers neither. A link element navigates where it says it does, and is
 * also what a middle-click and a screen reader expect.
 */
const AddressField = () => {
  const d = useRecordContext<Door>();
  if (!d) return null;
  return (
    <Link
      to={`${MARKETING_PATHS.doors}/${d.id}`}
      className="text-sm block hover:underline"
    >
      <div className="font-medium">{d.mail_to ?? "—"}</div>
      <div className="text-muted-foreground">
        {d.street}
        {d.city ? `, ${d.city}` : ""}
        {d.state ? `, ${d.state}` : ""} {d.zip5}
      </div>
    </Link>
  );
};

/**
 * The rating, and honest about its absence.
 *
 * 5,337 of 5,363 doors have no rating at all, so an empty cell is the norm.
 * Rendering that as anything judgmental -- a dash reading as "poor", a neutral
 * badge reading as "assessed and middling" -- would invent an assessment that
 * never happened. It says "unrated", quietly.
 */
const PotentialField = () => {
  const d = useRecordContext<Door>();
  if (!d) return null;
  return (
    <div className="flex flex-col gap-1 items-start">
      {d.potential ? (
        <Badge variant={d.potential === "high" ? "default" : "outline"}>
          {d.potential}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">unrated</span>
      )}
      {d.beyond_dsp && (
        <span className="text-[0.65rem] text-muted-foreground">
          + import/wholesale
        </span>
      )}
    </div>
  );
};

const StateField = () => {
  const d = useRecordContext<Door>();
  if (!d) return null;
  return (
    <div className="flex flex-row gap-1 flex-wrap items-center">
      <Badge variant={STATUS_TONE[d.status]}>{d.status}</Badge>
      {d.suppression_reason && (
        <Badge variant="outline">{d.suppression_reason}</Badge>
      )}
      {d.cell && <Badge variant="outline">{d.cell}</Badge>}
      {d.touches_sent > 0 && (
        <Badge variant="outline">
          {d.touches_sent} sent{d.last_touch ? ` · ${d.last_touch}` : ""}
        </Badge>
      )}
      {d.open_reviews > 0 && (
        <Badge variant="outline" className="border-orange-400">
          {d.open_reviews} to review
        </Badge>
      )}
    </div>
  );
};

/*
 * The opt-out control used to live here as a one-click button in the row. It
 * moved to the door's own page (DoorDetail).
 *
 * An opt-out is permanent and honored for post and email. Nothing that
 * permanent belongs one mis-click away in a table of 5,360 rows, beside a
 * Promote button it looked identical to, where the row under the cursor shifts
 * as a filter loads. On the detail page the door is already identified and its
 * history is on screen, so the act is deliberate by construction.
 */

/**
 * Promote a door to a prospect, carrying address_id — which is what later lets
 * the company page show what this door has already been mailed.
 *
 * door_directory already knows whether a company points here, so unlike the
 * first version this needs no extra query per row.
 */
const PromoteButton = () => {
  const door = useRecordContext<Door>();
  if (!door) return null;

  if (door.company_id) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/companies/${door.company_id}/show`}>
          {door.company_name ?? "View"}
        </Link>
      </Button>
    );
  }

  return (
    <Button variant="outline" size="sm" asChild>
      <Link
        to="/companies/create"
        state={{
          record: {
            name: door.mail_to ?? "",
            address: door.street,
            city: door.city ?? "",
            state_abbr: door.state ?? "",
            zipcode: door.zip5,
            country: "USA",
            address_id: door.id,
          },
        }}
        className="flex items-center gap-2"
      >
        <Building2 className="h-4 w-4" />
        Promote
      </Link>
    </Button>
  );
};

/**
 * One card per door, for narrow screens.
 *
 * The desktop view is a five-column DataTable beside a fixed 208px filter
 * sidebar. On a 400px phone that leaves the table about 40% of the width, so
 * the address wraps to four lines and the actions fall off the edge. A table
 * that has to be scrolled sideways to be read is not a table.
 *
 * Same data, same actions, stacked instead.
 */
const DoorCards = () => {
  const { data, isPending } = useListContext<Door>();
  if (isPending) return null;
  return (
    <div className="divide-y border rounded-md">
      {(data ?? []).map((door) => (
        <RecordContextProvider key={door.id} value={door}>
          <div className="p-3 space-y-2">
            <AddressField />
            <StateField />
            <div className="flex items-center gap-2">
              <PromoteButton />
            </div>
          </div>
        </RecordContextProvider>
      ))}
    </div>
  );
};

/**
 * Filters in a drawer on mobile.
 *
 * The sidebar is 208px of a 400px screen. Behind a button it costs nothing
 * until it is wanted, and the filter component itself is unchanged -- only
 * where it is rendered differs.
 */
const MobileFilterButton = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="outline" size="sm" className="mb-3">
        <SlidersHorizontal className="h-4 w-4 mr-2" />
        Filters
      </Button>
    </SheetTrigger>
    <SheetContent side="left" className="w-72 overflow-y-auto">
      <SheetHeader>
        <SheetTitle>Filter doors</SheetTitle>
      </SheetHeader>
      <div className="p-4">
        <DoorListFilter />
      </div>
    </SheetContent>
  </Sheet>
);

export function DoorList() {
  const isMobile = useIsMobile();

  return (
    <>
      {/* The page had no statement of what it is. "Doors" and a table of
          addresses does not say that this is the mailing universe rather than
          the customer list -- the distinction the whole schema rests on -- and
          the two row actions were an unlabeled icon and a word. */}
      <div className="pb-3">
        <h1 className="text-xl font-semibold">Doors</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Every physical address on the mailing list — <b>not</b> the customer
          list. All 5,360 get postcards; only the ones that respond become
          companies. Each door belongs to one{" "}
          <Explain term="cell">cell</Explain>, which is what decides when it is
          mailed, and one <Explain term="wave">wave</Explain>, which is only
          geography.
        </p>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          <b>Promote</b> turns a door into a company you can work — use it when
          someone responds; the company page then shows everything that door has
          been sent. <b>The ⊘ button</b> records an opt-out: permanent, honored
          for post and email, and never undone by a rebuild. For a bad address
          or a mail drop, use Review instead.
        </p>
      </div>
      <List
        resource="door_directory"
        actions={<DoorListActions />}
        sort={{ field: "mail_to", order: "ASC" }}
        perPage={25}
        title="Doors"
      >
        {isMobile ? (
          <div className="p-2">
            <MobileFilterButton />
            <DoorCards />
          </div>
        ) : (
          <div className="flex flex-row gap-8">
            <DoorListFilter />
            <div className="flex-1">
              <DataTable
                rowClick={false}
                bulkActionButtons={<DoorBulkActions />}
              >
                <DataTable.Col label="Door">
                  <AddressField />
                </DataTable.Col>
                <DataTable.Col
                  source="potential_rank"
                  label="Potential"
                  /* Sorted on the rank, rendered as the word. Sorting the text
                     would order high/low alphabetically -- right by accident
                     today, wrong the moment a third rating exists. */
                >
                  <PotentialField />
                </DataTable.Col>
                <DataTable.Col source="wave" label="Wave" />
                <DataTable.Col label="State">
                  <StateField />
                </DataTable.Col>
                <DataTable.Col label={false}>
                  <PromoteButton />
                </DataTable.Col>
              </DataTable>
            </div>
          </div>
        )}
      </List>
    </>
  );
}
